'use client';

import { useEffect, useMemo, useRef } from 'react';
import gsap from 'gsap/all';
import { Draggable } from 'gsap/Draggable';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useUi, Text, Image } from '@weareconceptstudio/core';

//* Components

//* Styles
import CategoriesSkeletonStyle from './style';

const CategoriesSkeleton = () => {
	const { winWidth } = useUi();

	//! Refs
	const scrollTriggerDown = useRef();
	const scrollTriggerUP = useRef();
	const downCategories = useRef();
	const upCategories = useRef();
	const wrapper = useRef();
	const refProgress = useRef();
	const directionRef = useRef();
	const translatableX = useRef();
	const draggableUp = useRef();
	const draggableDown = useRef();

	const categories = false;

	//! Scroll and Drag
	useEffect(() => {
		const sizeTranslate = upCategories.current.offsetWidth - winWidth;

		if (sizeTranslate > 0) {
			function createScrollTrigger({ reverse, trigger, item, x, name }) {
				const direction = reverse ? 1 : -1;

				//! leave back enter back
				function reCreatEScrollTrigger() {
					ScrollTrigger.getById('item-categories-up')?.kill();
					ScrollTrigger.getById('item-categories-down')?.kill();

					scrollTriggerUP.current = createScrollTrigger({
						trigger: upCategories.current,
						item: upCategories.current,
						name: 'up',
					});

					scrollTriggerDown.current = createScrollTrigger({
						reverse: true,
						trigger: upCategories.current,
						item: downCategories.current,
						name: 'down',
					});
				}

				//! Trigger
				return ScrollTrigger.create({
					start: 'top bottom',
					end: `220% top`,
					scrub: true,
					id: `item-categories-${name}`,
					// markers: true,
					trigger: trigger,
					onUpdate: (self) => {
						if (x || x == 0) {
							//! After drag
							if (!refProgress.current) {
								refProgress.current = self.progress;
							}

							const transformItem = item.style.transform;
							const match = transformItem.match(/translate3d\(([\d.-]+)px/);
							const xTranslate = match ? parseFloat(match[1]) : null;

							if (!directionRef.current || self.direction !== directionRef.current) {
								directionRef.current = self.direction;
								refProgress.current = self.progress;
								translatableX.current = xTranslate;
							}

							if (self.progress !== refProgress.current) {
								if (self.direction > 0 && self.progress !== refProgress.current) {
									const normalizedProgress =
										(self.progress - refProgress.current) / (1 - refProgress.current);
									if (translatableX.current < sizeTranslate) {
										gsap.set(item, {
											x:
												translatableX.current * direction +
												(sizeTranslate - Math.abs(translatableX.current)) *
													-direction *
													normalizedProgress,
											duration: 0,
										});
									}
								} else {
									const normalizedProgress =
										(refProgress.current - self.progress) / refProgress.current;
									gsap.set(item, {
										x:
											translatableX.current * direction * -1 -
											Math.abs(translatableX.current) * -direction * normalizedProgress,
										duration: 0,
									});
								}
							}
						} else {
							//! not drag
							const progress = self.progress;
							const translateX = sizeTranslate * direction * progress;
							gsap.set(item, { x: translateX, duration: 0 });
						}
					},

					onEnterBack: () => {
						reCreatEScrollTrigger();
					},
					onLeaveBack: () => {
						reCreatEScrollTrigger();
					},
				});
			}

			//! created scrollTrigger
			scrollTriggerUP.current = createScrollTrigger({
				trigger: upCategories.current,
				item: upCategories.current,
				name: 'up',
			});

			scrollTriggerDown.current = createScrollTrigger({
				reverse: true,
				trigger: upCategories.current,
				item: downCategories.current,
				name: 'down',
			});

			function createDraggable({ item, bounds, direction = 1 }) {
				return Draggable.create(item, {
					type: 'x',
					// bounds: { minX: -sizeTranslate, maxX: 0 },
					bounds: bounds,
					inertia: true,
					onDragStart: () => {
						ScrollTrigger.getById('item-categories-up')?.kill();
						ScrollTrigger.getById('item-categories-down')?.kill();
					},
					onDrag: function () {
						gsap.set(upCategories.current, { x: direction * this.x });
						gsap.set(downCategories.current, { x: direction * -this.x });
					},
					onDragEnd: function () {
						refProgress.current = false;
						translatableX.current = this.x;
						scrollTriggerUP.current = createScrollTrigger({
							trigger: upCategories.current,
							item: upCategories.current,
							reverse: true,
							name: 'up',
							x: this.x,
						});
						scrollTriggerDown.current = createScrollTrigger({
							trigger: upCategories.current,
							item: downCategories.current,
							name: 'down',
							x: this.x,
						});
					},
				});
			}

			//! create draggable
			draggableUp.current = createDraggable({
				item: upCategories.current,
				bounds: '.wrapper-data-categories',
			});
			draggableDown.current = createDraggable({
				item: downCategories.current,
				bounds: '.wrapper-data-categories',
				direction: -1,
			});
		}

		return () => {
			ScrollTrigger.getById('item-categories-up')?.kill();
			ScrollTrigger.getById('item-categories-down')?.kill();

			if (draggableUp.current) {
				draggableUp.current.forEach((draggable) => {
					draggable.kill();
				});
				draggableDown.current.forEach((draggable) => {
					draggable.kill();
				});
			}
		};
	}, [winWidth, categories]);

	//! ltr rtl data
	const [upDataJSX, downDataJSX] = useMemo(() => {
		const skeletonUpData = Array.from({ length: 10 }).map((item, i) => {
			return (
				<div
					key={i}
					className='skeleton-item'>
					<Image src={'/images/product-home.svg'} />
				</div>
			);
		});

		return [skeletonUpData, skeletonUpData];
	}, [categories]);

	return (
		<CategoriesSkeletonStyle className={`skeleton`}>
			<div className='wrapper-title'>
				<Text
					tag={'h2'}
					className={'h2 title-categories uppercase'}
					text={'ourCategories'}
				/>
			</div>

			<div
				className='wrapper-data-categories'
				ref={wrapper}>
				<div
					className='data-wrapper up-data'
					ref={upCategories}>
					{upDataJSX}
				</div>
				<div className='wrapper-down'>
					<div
						className='data-wrapper down-data'
						ref={downCategories}>
						{downDataJSX}
					</div>
				</div>
			</div>
		</CategoriesSkeletonStyle>
	);
};

export default CategoriesSkeleton;
